import React, { useEffect, useState } from 'react';
import {
  BuildingLibraryIcon,
  PhoneIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import {
  fetchVendorBankDetails,
  createVendorBankDetails,
  updateVendorBankDetails,
  verifyVendorBankDetails,
  verifyIfscCode,
  VendorBankDetails,
  VendorBankDetailsInput,
} from '../../services/MasterAdmin/vendorBankDetailsService';

interface VendorBankDetailsModalProps {
  vendorId: string;
  vendorName?: string;
  isOpen: boolean;
  onClose: () => void;
}

const labelClass = 'text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider';
const valueClass = 'text-sm text-gray-900 dark:text-white break-words';

const emptyForm = (): VendorBankDetailsInput => ({
  accountHolderName: '',
  accountNumber: '',
  ifscCode: '',
  bankName: '',
  branchName: '',
  accountType: 'SAVINGS',
  mobileNumber: '',
});

const toForm = (row: VendorBankDetails): VendorBankDetailsInput => ({
  accountHolderName: row.accountHolderName || '',
  accountNumber: row.accountNumber || '',
  ifscCode: row.ifscCode || '',
  bankName: row.bankName || '',
  branchName: row.branchName || '',
  accountType: (row.accountType as VendorBankDetailsInput['accountType']) || 'SAVINGS',
  mobileNumber: row.mobileNumber || '',
});

const errMessage = (e: unknown, fallback: string): string => {
  const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return typeof msg === 'string' && msg.trim() ? msg : fallback;
};

/** Same pattern as backend `ifscVerification.controller` */
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;

const VendorBankDetailsModal: React.FC<VendorBankDetailsModalProps> = ({
  vendorId,
  vendorName,
  isOpen,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bankDetails, setBankDetails] = useState<VendorBankDetails | null>(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<VendorBankDetailsInput>(emptyForm);
  const [ifscVerifying, setIfscVerifying] = useState(false);
  const [ifscLookupError, setIfscLookupError] = useState<string | null>(null);

  const load = async () => {
    if (!vendorId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await fetchVendorBankDetails(vendorId);
      setBankDetails(data);
      if (data) {
        setForm(toForm(data));
        setEditing(false);
      } else {
        setForm(emptyForm());
        setEditing(true);
      }
    } catch (e: unknown) {
      setError(errMessage(e, 'Failed to load bank details.'));
      setBankDetails(null);
      setForm(emptyForm());
      setEditing(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !vendorId) return;
    load();
  }, [isOpen, vendorId]);

  /** Debounced IFSC lookup: fills branch + bank from `/api/banking/verify-ifsc/:ifscCode` */
  useEffect(() => {
    if (!isOpen || loading) return;
    const showBankForm = !bankDetails || editing;
    if (!showBankForm) return;

    const code = form.ifscCode.trim().toUpperCase();
    if (!IFSC_REGEX.test(code)) {
      setIfscLookupError(null);
      return;
    }
    const t = window.setTimeout(async () => {
      try {
        setIfscVerifying(true);
        setIfscLookupError(null);
        const data = await verifyIfscCode(code);
        setForm((prev) => ({
          ...prev,
          ifscCode: code,
          branchName: data.branchName || prev.branchName,
          bankName: data.bankName || prev.bankName,
        }));
      } catch (e: unknown) {
        setIfscLookupError(errMessage(e, 'Could not verify IFSC code.'));
      } finally {
        setIfscVerifying(false);
      }
    }, 450);
    return () => window.clearTimeout(t);
  }, [form.ifscCode, isOpen, loading, bankDetails, editing]);

  const handleField = (field: keyof VendorBankDetailsInput, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!vendorId) return;
    const code = form.ifscCode.trim().toUpperCase();
    if (!IFSC_REGEX.test(code)) {
      setError('Enter a valid 11-character IFSC code (e.g. HDFC0001234).');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      let verified;
      try {
        verified = await verifyIfscCode(code);
      } catch (e: unknown) {
        setError(errMessage(e, 'IFSC could not be verified. Please check and try again.'));
        return;
      }
      const bankName = form.bankName.trim() || verified.bankName || '';
      if (!bankName) {
        setError('Bank name is required. It can be filled automatically after a valid IFSC lookup.');
        return;
      }
      const branchName = form.branchName?.trim() || verified.branchName || undefined;
      if (!bankDetails) {
        const created = await createVendorBankDetails(vendorId, {
          ...form,
          ifscCode: code,
          bankName,
          branchName,
          mobileNumber: form.mobileNumber?.trim() || undefined,
        });
        setBankDetails(created);
        setForm(toForm(created));
        setEditing(false);
      } else {
        const updated = await updateVendorBankDetails(vendorId, {
          ...form,
          ifscCode: code,
          bankName,
          branchName,
          mobileNumber: form.mobileNumber?.trim() || undefined,
        });
        setBankDetails(updated);
        setForm(toForm(updated));
        setEditing(false);
      }
    } catch (e: unknown) {
      setError(errMessage(e, 'Could not save bank details.'));
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async (verify: boolean) => {
    if (!vendorId) return;
    try {
      setVerifying(true);
      setError(null);
      const updated = await verifyVendorBankDetails(vendorId, verify);
      setBankDetails(updated);
      setForm(toForm(updated));
    } catch (e: unknown) {
      setError(errMessage(e, 'Could not update verification status.'));
    } finally {
      setVerifying(false);
    }
  };

  if (!isOpen) return null;

  const hasRecord = !!bankDetails;
  const showForm = !hasRecord || editing;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border border-gray-100 dark:border-gray-700">
        <div className="p-5 border-b dark:border-gray-700 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg shrink-0">
              <BuildingLibraryIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Vendor bank details</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{vendorName || vendorId}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-2xl leading-none">
            ×
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {loading ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading bank details...</p>
          ) : (
            <>
              {error && (
                <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              )}

              {showForm ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {hasRecord
                      ? 'Update the account information below.'
                      : 'No bank details on file. Add account information for this vendor.'}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <label className={labelClass}>Account holder name *</label>
                      <input
                        value={form.accountHolderName}
                        onChange={(e) => handleField('accountHolderName', e.target.value)}
                        className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Account number *</label>
                      <input
                        value={form.accountNumber}
                        onChange={(e) => handleField('accountNumber', e.target.value)}
                        className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>IFSC code *</label>
                      <div className="mt-1 flex items-center gap-2">
                        <input
                          value={form.ifscCode}
                          maxLength={11}
                          placeholder="11 characters"
                          onChange={(e) => handleField('ifscCode', e.target.value.toUpperCase())}
                          className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                        />
                        {ifscVerifying && (
                          <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0 whitespace-nowrap">
                            Verifying…
                          </span>
                        )}
                      </div>
                      {ifscLookupError && (
                        <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">{ifscLookupError}</p>
                      )}
                      {!ifscLookupError && IFSC_REGEX.test(form.ifscCode.trim()) && !ifscVerifying && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Branch and bank name are filled from IFSC when valid.
                        </p>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelClass}>Bank name *</label>
                      <input
                        value={form.bankName}
                        onChange={(e) => handleField('bankName', e.target.value)}
                        className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelClass}>Branch name</label>
                      <input
                        value={form.branchName || ''}
                        onChange={(e) => handleField('branchName', e.target.value)}
                        className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Account type</label>
                      <select
                        value={form.accountType}
                        onChange={(e) => handleField('accountType', e.target.value as VendorBankDetailsInput['accountType'])}
                        className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                      >
                        <option value="SAVINGS">Savings</option>
                        <option value="CURRENT">Current</option>
                        <option value="OTHER">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Mobile number</label>
                      <input
                        value={form.mobileNumber || ''}
                        onChange={(e) => handleField('mobileNumber', e.target.value)}
                        className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                bankDetails && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <p className={labelClass}>Account holder name</p>
                        <p className={valueClass}>{bankDetails.accountHolderName || '—'}</p>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <p className={labelClass}>Account number</p>
                        <p className={valueClass}>{bankDetails.accountNumber || '—'}</p>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <p className={labelClass}>IFSC code</p>
                        <p className={valueClass}>{bankDetails.ifscCode || '—'}</p>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <p className={labelClass}>Bank name</p>
                        <p className={valueClass}>{bankDetails.bankName || '—'}</p>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <p className={labelClass}>Branch name</p>
                        <p className={valueClass}>{bankDetails.branchName || '—'}</p>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <p className={labelClass}>Account type</p>
                        <p className={valueClass}>{bankDetails.accountType || '—'}</p>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <p className={labelClass}>Mobile number</p>
                        <p className={`flex items-center gap-2 ${valueClass}`}>
                          <PhoneIcon className="h-4 w-4 text-gray-400 shrink-0" />
                          {bankDetails.mobileNumber || '—'}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <p className={labelClass}>Verification status</p>
                        <p className={`flex items-center gap-2 ${valueClass}`}>
                          {bankDetails.isVerified ? (
                            <>
                              <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />
                              Verified
                              {bankDetails.verifiedAt && (
                                <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">
                                  ({new Date(bankDetails.verifiedAt).toLocaleString()})
                                </span>
                              )}
                            </>
                          ) : (
                            <>
                              <XCircleIcon className="h-4 w-4 text-yellow-600 dark:text-yellow-400 shrink-0" />
                              Not verified
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                )
              )}
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t dark:border-gray-700 flex flex-wrap items-center justify-end gap-2 shrink-0">
          {loading ? null : showForm ? (
            <>
              {hasRecord && (
                <button
                  type="button"
                  onClick={() => {
                    setEditing(false);
                    if (bankDetails) setForm(toForm(bankDetails));
                    setError(null);
                    setIfscLookupError(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 mr-auto"
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Close
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : hasRecord ? 'Update' : 'Add bank details'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Close
              </button>
              {bankDetails && (
                <>
                  {bankDetails.isVerified ? (
                    <button
                      type="button"
                      disabled={verifying}
                      onClick={() => handleVerify(false)}
                      className="px-4 py-2 text-sm font-medium text-amber-800 dark:text-amber-200 bg-amber-100 dark:bg-amber-900/40 rounded-md hover:bg-amber-200 dark:hover:bg-amber-900/60 disabled:opacity-50"
                    >
                      {verifying ? '…' : 'Mark not verified'}
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={verifying}
                      onClick={() => handleVerify(true)}
                      className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {verifying ? '…' : 'Verify bank details'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(true);
                      setError(null);
                    }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/50"
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                    Update details
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorBankDetailsModal;
